import { useState, useEffect, useMemo } from 'react';
import {
  Webhook, Plus, Trash2, Send, Copy, Loader2, AlertTriangle,
  CheckCircle, XCircle, Eye, Power, Clock, Activity, ShieldCheck,
  Zap, RefreshCw, ChevronRight, Sparkles, Globe, KeyRound,
} from 'lucide-react';
import {
  listOutboundWebhooks, getOutboundWebhookEventTypes, createOutboundWebhook,
  updateOutboundWebhook, deleteOutboundWebhook, testOutboundWebhook,
  getOutboundWebhookDeliveries,
} from '../services/api';
import { InfoTip } from '../components/ui';

// Group event types by domain for the picker UX
function groupEventTypes(types) {
  const groups = { smart_trade: [], grid_bot: [], dca_safety: [], alert: [], regime: [] };
  for (const t of types || []) {
    const key = t.id.split('.')[0];
    if (groups[key]) groups[key].push(t);
    else groups.regime.push(t);
  }
  return groups;
}

const GROUP_META = {
  smart_trade: { label: 'Smart Trade',  color: 'text-amber-400 border-amber-500/30' },
  grid_bot:    { label: 'Grid Bot',     color: 'text-emerald-400 border-emerald-500/30' },
  dca_safety:  { label: 'DCA Safety',   color: 'text-violet-400 border-violet-500/30' },
  alert:       { label: 'Alerts',       color: 'text-cyan-400 border-cyan-500/30' },
  regime:      { label: 'Regime',       color: 'text-pink-400 border-pink-500/30' },
};

function fmtRelative(iso) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return 'now';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export default function OutboundWebhooksPage() {
  const [webhooks, setWebhooks] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [deliveriesLoading, setDeliveriesLoading] = useState(false);
  const [testingId, setTestingId] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [list, types] = await Promise.all([
        listOutboundWebhooks().then((r) => r?.data || []),
        getOutboundWebhookEventTypes().then((r) => r?.data || []),
      ]);
      setWebhooks(list);
      setEventTypes(types);
    } catch {} finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function loadDeliveries(id) {
    setSelectedId(id);
    setDeliveriesLoading(true);
    try {
      const res = await getOutboundWebhookDeliveries(id);
      setDeliveries(res?.data?.deliveries || []);
    } catch {} finally { setDeliveriesLoading(false); }
  }

  async function handleTest(id) {
    setTestingId(id);
    try {
      await testOutboundWebhook(id);
      // Give the dispatcher 1.5s to deliver before refreshing the log
      setTimeout(async () => {
        await loadDeliveries(id);
        await load();
        setTestingId(null);
      }, 1500);
    } catch (err) {
      alert('Test failed: ' + err.message);
      setTestingId(null);
    }
  }

  async function handleDelete(id) {
    if (!confirm('Delete this webhook? Future events will no longer fire to this URL.')) return;
    await deleteOutboundWebhook(id);
    if (selectedId === id) { setSelectedId(null); setDeliveries([]); }
    await load();
  }

  async function handleToggle(wh) {
    await updateOutboundWebhook(wh.id, { enabled: !wh.enabled });
    await load();
  }

  async function handleFormatChange(wh, newFormat) {
    await updateOutboundWebhook(wh.id, { format: newFormat });
    await load();
  }

  // Aggregate stats across all webhooks for the hero band
  const stats = useMemo(() => {
    const total = webhooks.length;
    const enabled = webhooks.filter((w) => w.enabled).length;
    const totalDeliveries = webhooks.reduce((s, w) => s + (w.deliveryCount || 0), 0);
    const totalFailures = webhooks.reduce((s, w) => s + (w.failureCount || 0), 0);
    const successRate = totalDeliveries > 0
      ? Math.round(((totalDeliveries - totalFailures) / totalDeliveries) * 1000) / 10
      : null;
    const mostRecent = webhooks
      .map((w) => w.lastDeliveryAt)
      .filter(Boolean)
      .sort((a, b) => new Date(b) - new Date(a))[0];
    return { total, enabled, totalDeliveries, totalFailures, successRate, mostRecent };
  }, [webhooks]);

  const selectedWebhook = webhooks.find((w) => w.id === selectedId);

  return (
    <div className="space-y-6 animate-fade-in max-w-7xl">
      {/* ─── Hero ─────────────────────────────────────────────────────── */}
      <div className="relative overflow-hidden rounded-2xl border border-cyan-500/20 bg-gradient-to-br from-cyan-500/10 via-dark-800/40 to-dark-800/40 p-6">
        <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-cyan-400/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full bg-violet-400/5 blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center shadow-lg shadow-cyan-500/20">
                <Webhook className="w-5 h-5 text-dark-900" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Outbound Webhooks</h1>
                <p className="text-xs text-cyan-300/80 mt-0.5 flex items-center gap-1.5">
                  <ShieldCheck className="w-3 h-3" /> Real bot events · HMAC-SHA256 signed · Slack / Discord / Zapier / n8n compatible
                  <InfoTip side="bottom">Outbound = haythix pushes events OUT to your tools. Inbound (TV Webhooks) = external alerts trigger trades. Opposite directions.</InfoTip>
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowCreate(true)}
              disabled={webhooks.length >= 20}
              className="flex items-center gap-1.5 bg-gradient-to-r from-cyan-500 to-blue-400 hover:from-cyan-400 hover:to-blue-300 disabled:from-dark-600 disabled:to-dark-600 disabled:text-gray-500 text-dark-900 font-semibold text-sm px-4 py-2.5 rounded-xl shadow-lg shadow-cyan-500/20"
            >
              <Plus className="w-4 h-4" /> {webhooks.length >= 20 ? 'Limit reached (20)' : 'New Webhook'}
            </button>
          </div>

          {/* Aggregate stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
            <HeroStat icon={Webhook}     label="Webhooks"        value={`${stats.enabled}/${stats.total}`} sub={stats.total > 0 ? `${stats.enabled} enabled` : 'none yet'} accent="text-cyan-400" />
            <HeroStat icon={Send}        label="Total deliveries" value={stats.totalDeliveries.toLocaleString()} accent="text-violet-400" />
            <HeroStat icon={CheckCircle} label="Success rate"     value={stats.successRate != null ? `${stats.successRate}%` : '—'} sub={stats.totalFailures > 0 ? `${stats.totalFailures} failures` : null} accent={stats.successRate == null || stats.successRate >= 95 ? 'text-emerald-400' : stats.successRate >= 80 ? 'text-amber-400' : 'text-red-400'} />
            <HeroStat icon={Activity}    label="Last delivery"    value={fmtRelative(stats.mostRecent) || '—'} accent="text-amber-400" />
          </div>
        </div>
      </div>

      {/* ─── Create form (slides in at top) ────────────────────────────── */}
      {showCreate && (
        <CreateForm eventTypes={eventTypes} onCancel={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />
      )}

      {/* ─── Body: list + detail (or full empty state) ─────────────────── */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-7 h-7 animate-spin text-cyan-400" />
        </div>
      ) : webhooks.length === 0 ? (
        <EmptyState onCreate={() => setShowCreate(true)} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Webhook list */}
          <div className="lg:col-span-1 space-y-2">
            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold px-1">Your webhooks</p>
            {webhooks.map((wh) => (
              <WebhookListCard
                key={wh.id}
                webhook={wh}
                selected={selectedId === wh.id}
                onClick={() => loadDeliveries(wh.id)}
              />
            ))}
          </div>

          {/* Detail panel */}
          <div className="lg:col-span-2">
            {selectedWebhook ? (
              <DetailView
                webhook={selectedWebhook}
                deliveries={deliveries}
                deliveriesLoading={deliveriesLoading}
                onTest={() => handleTest(selectedWebhook.id)}
                onToggle={() => handleToggle(selectedWebhook)}
                onDelete={() => handleDelete(selectedWebhook.id)}
                onRefresh={() => loadDeliveries(selectedWebhook.id)}
                onFormatChange={(fmt) => handleFormatChange(selectedWebhook, fmt)}
                testing={testingId === selectedWebhook.id}
              />
            ) : (
              <div className="bg-dark-800/40 rounded-2xl border border-dark-700/50 p-12 text-center h-full flex flex-col items-center justify-center">
                <Zap className="w-10 h-10 text-gray-600 mb-3" />
                <p className="text-sm text-gray-400 font-medium">Select a webhook</p>
                <p className="text-xs text-gray-600 mt-1">Click any webhook on the left to see delivery history.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── Event types catalog (collapsible help) ────────────────────── */}
      {webhooks.length > 0 && eventTypes.length > 0 && (
        <EventTypesHelp eventTypes={eventTypes} />
      )}
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function HeroStat({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="bg-dark-900/40 backdrop-blur-sm rounded-xl border border-dark-700/40 p-3">
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-3.5 h-3.5 ${accent || 'text-gray-400'}`} />
        <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{label}</p>
      </div>
      <p className={`text-xl font-bold font-mono ${accent || 'text-white'}`}>{value}</p>
      {sub && <p className="text-[10px] text-gray-600 mt-0.5">{sub}</p>}
    </div>
  );
}

function WebhookListCard({ webhook, selected, onClick }) {
  // Health dot color: green if last delivery success, red if last failure, gray if never fired
  const dotColor =
    webhook.lastDeliveryAt == null ? 'bg-gray-500' :
    webhook.lastDeliverySuccess === false ? 'bg-red-400' :
    webhook.lastDeliverySuccess === true ? 'bg-emerald-400' :
    'bg-gray-500';

  const failurePct = webhook.deliveryCount > 0
    ? Math.round((webhook.failureCount / webhook.deliveryCount) * 100)
    : 0;

  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-4 rounded-2xl border transition-all ${
        selected
          ? 'bg-cyan-500/10 border-cyan-400/40 shadow-lg shadow-cyan-500/10'
          : 'bg-dark-800/40 border-dark-700/50 hover:border-dark-600'
      }`}
    >
      <div className="flex items-start gap-2 mb-1.5">
        <div className="relative mt-1 flex-shrink-0">
          <div className={`w-2 h-2 rounded-full ${dotColor}`} />
          {webhook.enabled && webhook.lastDeliverySuccess === true && (
            <div className={`absolute inset-0 w-2 h-2 rounded-full ${dotColor} animate-ping opacity-60`} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-bold text-white truncate">{webhook.name}</span>
            {!webhook.enabled && (
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-gray-500/15 text-gray-400 border border-gray-500/20 flex-shrink-0">
                OFF
              </span>
            )}
          </div>
          <p className="text-[10px] text-gray-500 font-mono truncate">{webhook.url}</p>
        </div>
      </div>

      <div className="flex items-center gap-3 mt-2 text-[10px]">
        <span className="text-gray-400">
          <span className="text-cyan-400 font-mono">{webhook.events?.length || 0}</span> event{webhook.events?.length !== 1 ? 's' : ''}
        </span>
        <span className="text-gray-600">·</span>
        <span className="text-gray-400">
          <span className="text-white font-mono">{webhook.deliveryCount || 0}</span> sent
        </span>
        {failurePct > 0 && (
          <>
            <span className="text-gray-600">·</span>
            <span className="text-red-400">
              <span className="font-mono">{failurePct}%</span> failed
            </span>
          </>
        )}
        {webhook.lastDeliveryAt && (
          <span className="ml-auto text-gray-500">{fmtRelative(webhook.lastDeliveryAt)}</span>
        )}
      </div>
    </button>
  );
}

function DetailView({ webhook, deliveries, deliveriesLoading, onTest, onToggle, onDelete, onRefresh, onFormatChange, testing }) {
  if (!webhook) return null;

  const successCount = (webhook.deliveryCount || 0) - (webhook.failureCount || 0);
  const successRate = webhook.deliveryCount > 0
    ? Math.round((successCount / webhook.deliveryCount) * 1000) / 10
    : null;

  // Suggest a format if the URL looks like Discord/Slack/Telegram but the format
  // is set to something else. Surfacing this prevents the "100% failed because
  // wrong format" mystery error.
  const currentFormat = webhook.format || 'raw';
  let formatMismatch = null;
  if (webhook.url) {
    const looksDiscord = webhook.url.includes('discord.com/api/webhooks') || webhook.url.includes('discordapp.com/api/webhooks');
    const looksSlack = webhook.url.includes('hooks.slack.com');
    const looksTelegram = webhook.url.includes('api.telegram.org/bot');
    if (looksDiscord && currentFormat !== 'discord') formatMismatch = 'discord';
    else if (looksSlack && currentFormat !== 'slack') formatMismatch = 'slack';
    else if (looksTelegram && currentFormat !== 'telegram') formatMismatch = 'telegram';
  }

  return (
    <div className="space-y-4">
      {/* Header card */}
      <div className="bg-dark-800/40 rounded-2xl border border-dark-700/50 p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-lg font-bold text-white truncate">{webhook.name}</h3>
              {webhook.enabled ? (
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                  ENABLED
                </span>
              ) : (
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-500/15 text-gray-400 border border-gray-500/30">
                  DISABLED
                </span>
              )}
            </div>
            <p className="text-xs text-gray-400 font-mono break-all flex items-center gap-1.5">
              <Globe className="w-3 h-3 text-gray-500 flex-shrink-0" />
              {webhook.url}
            </p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button
              onClick={onTest}
              disabled={testing}
              className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20 disabled:opacity-50"
            >
              {testing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
              {testing ? 'Testing…' : 'Send test'}
            </button>
            <button
              onClick={onToggle}
              className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-lg border border-dark-600 text-gray-300 hover:border-dark-500"
            >
              <Power className="w-3 h-3" /> {webhook.enabled ? 'Disable' : 'Enable'}
            </button>
            <button
              onClick={onDelete}
              className="p-2 rounded-lg text-gray-500 hover:text-red-400 hover:bg-red-500/10 border border-dark-600 hover:border-red-500/30"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
          <div className="bg-dark-700/30 rounded-lg p-2.5">
            <p className="text-[9px] uppercase tracking-wider text-gray-500">Deliveries</p>
            <p className="text-base font-bold font-mono text-white mt-0.5">{webhook.deliveryCount || 0}</p>
          </div>
          <div className="bg-dark-700/30 rounded-lg p-2.5">
            <p className="text-[9px] uppercase tracking-wider text-gray-500">Success rate</p>
            <p className={`text-base font-bold font-mono mt-0.5 ${successRate == null || successRate >= 95 ? 'text-emerald-400' : successRate >= 80 ? 'text-amber-400' : 'text-red-400'}`}>
              {successRate != null ? `${successRate}%` : '—'}
            </p>
          </div>
          <div className="bg-dark-700/30 rounded-lg p-2.5">
            <p className="text-[9px] uppercase tracking-wider text-gray-500">Failures</p>
            <p className={`text-base font-bold font-mono mt-0.5 ${webhook.failureCount > 0 ? 'text-red-400' : 'text-gray-400'}`}>
              {webhook.failureCount || 0}
            </p>
          </div>
          <div className="bg-dark-700/30 rounded-lg p-2.5">
            <p className="text-[9px] uppercase tracking-wider text-gray-500">Last fired</p>
            <p className="text-sm font-bold font-mono text-amber-400 mt-0.5">{fmtRelative(webhook.lastDeliveryAt) || 'Never'}</p>
          </div>
        </div>

        {/* Subscribed events */}
        <div>
          <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1.5 font-semibold">Subscribed events</p>
          <div className="flex flex-wrap gap-1">
            {webhook.events?.map((e) => {
              const group = e === '*' ? null : (GROUP_META[e.split('.')[0]] || GROUP_META.regime);
              return (
                <span
                  key={e}
                  className={`text-[10px] font-mono px-2 py-1 rounded border ${
                    e === '*'
                      ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30 font-bold'
                      : group
                        ? `bg-dark-700/40 ${group.color}`
                        : 'bg-dark-700 text-gray-300 border-dark-600'
                  }`}
                >
                  {e === '*' ? 'ALL EVENTS' : e}
                </span>
              );
            })}
          </div>
        </div>

        {/* HMAC info */}
        {webhook.secret && (
          <div className="mt-3 pt-3 border-t border-dark-700/50 flex items-center gap-2 text-[11px] text-gray-400">
            <KeyRound className="w-3 h-3 text-emerald-400" />
            <span>HMAC-SHA256 secret ending in <span className="font-mono text-gray-300">{webhook.secret}</span></span>
          </div>
        )}

        {/* Payload format picker (editable inline) */}
        <div className="mt-3 pt-3 border-t border-dark-700/50">
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Payload format</p>
            {formatMismatch && (
              <span className="text-[10px] text-amber-400 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                URL looks like {formatMismatch} — click to fix
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            {FORMAT_OPTIONS.map((opt) => {
              const active = currentFormat === opt.id;
              const suggested = formatMismatch === opt.id;
              // Disable 'custom' on the detail picker — switching to custom from
              // detail panel without a template would break the webhook. User
              // should delete + recreate to switch into custom.
              const disabled = opt.id === 'custom' && currentFormat !== 'custom';
              return (
                <button
                  key={opt.id}
                  onClick={() => !disabled && onFormatChange(opt.id)}
                  disabled={disabled}
                  title={disabled ? 'To use custom template, create a new webhook with custom format' : ''}
                  className={`text-left p-2 rounded-lg border transition-all text-xs ${
                    active
                      ? 'bg-cyan-500/15 border-cyan-400/40 text-cyan-300'
                      : suggested
                        ? 'bg-amber-500/10 border-amber-400/40 text-amber-300 ring-1 ring-amber-400/30 animate-pulse'
                        : disabled
                          ? 'bg-dark-800/50 border-dark-700 text-gray-600 cursor-not-allowed'
                          : 'bg-dark-700 border-dark-600 text-gray-400 hover:border-dark-500'
                  }`}
                >
                  <span className="font-bold">{opt.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Deliveries log */}
      <div className="bg-dark-800/40 rounded-2xl border border-dark-700/50 p-5">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Recent deliveries</p>
          <button
            onClick={onRefresh}
            disabled={deliveriesLoading}
            className="flex items-center gap-1 text-[10px] text-gray-500 hover:text-white disabled:opacity-50"
          >
            <RefreshCw className={`w-3 h-3 ${deliveriesLoading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>

        {deliveriesLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-cyan-400" />
          </div>
        ) : deliveries.length === 0 ? (
          <div className="bg-dark-700/20 rounded-xl p-6 text-center">
            <Activity className="w-8 h-8 text-gray-600 mx-auto mb-2" />
            <p className="text-xs text-gray-500 mb-1">No deliveries yet</p>
            <p className="text-[10px] text-gray-600">Click <strong className="text-emerald-400">Send test</strong> above to fire a sample event.</p>
          </div>
        ) : (
          <div className="space-y-1 max-h-96 overflow-y-auto">
            {deliveries.map((d, i) => (
              <DeliveryRow key={i} delivery={d} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DeliveryRow({ delivery }) {
  const success = !!delivery.success;
  return (
    <div className={`flex items-center gap-3 rounded-lg p-2.5 text-xs ${success ? 'bg-dark-700/20 hover:bg-dark-700/40' : 'bg-red-500/5 border border-red-500/15'}`}>
      {success ? (
        <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
      ) : (
        <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
      )}
      <span className="font-mono text-gray-200 flex-1 truncate">{delivery.eventType || 'event'}</span>
      <span className={`font-mono text-[10px] flex-shrink-0 ${success ? 'text-emerald-400' : 'text-red-400'}`}>
        {delivery.statusCode != null ? `HTTP ${delivery.statusCode}` : (delivery.errorMsg?.slice(0, 24) || '—')}
      </span>
      {delivery.elapsedMs != null && (
        <span className="font-mono text-[10px] text-gray-500 flex-shrink-0 flex items-center gap-0.5">
          <Clock className="w-2.5 h-2.5" />{delivery.elapsedMs}ms
        </span>
      )}
      <span className="font-mono text-[10px] text-gray-500 flex-shrink-0">
        {delivery.deliveredAt ? new Date(delivery.deliveredAt).toLocaleTimeString() : '—'}
      </span>
    </div>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div className="bg-dark-800/40 rounded-2xl border border-dark-700/50 p-12 text-center">
      <div className="w-16 h-16 rounded-2xl bg-dark-700 mx-auto mb-4 flex items-center justify-center">
        <Webhook className="w-8 h-8 text-gray-600" />
      </div>
      <p className="text-white font-medium mb-1">No webhooks registered</p>
      <p className="text-sm text-gray-500 mb-4 max-w-md mx-auto">
        Push real bot events (Smart Trade fills, Grid Bot trades, DCA safety orders, alerts) to any HTTPS endpoint — Slack, Discord, Zapier, n8n, your own server.
      </p>
      <button
        onClick={onCreate}
        className="inline-flex items-center gap-1.5 bg-gradient-to-r from-cyan-500 to-blue-400 hover:from-cyan-400 hover:to-blue-300 text-dark-900 font-semibold text-sm px-4 py-2 rounded-lg shadow-lg shadow-cyan-500/20"
      >
        <Plus className="w-4 h-4" /> Create first webhook
      </button>
    </div>
  );
}

function EventTypesHelp({ eventTypes }) {
  const [open, setOpen] = useState(false);
  const groups = groupEventTypes(eventTypes);

  return (
    <div className="bg-dark-800/30 rounded-2xl border border-dark-700/50 overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full p-4 flex items-center justify-between hover:bg-dark-700/20"
      >
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-cyan-400" />
          <span className="text-sm font-semibold text-white">Available event types</span>
          <span className="text-[10px] text-gray-500 font-mono">{eventTypes.length} events</span>
        </div>
        <ChevronRight className={`w-4 h-4 text-gray-500 transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && (
        <div className="p-4 pt-0 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {Object.entries(groups).map(([groupKey, types]) => {
            if (types.length === 0) return null;
            const meta = GROUP_META[groupKey];
            return (
              <div key={groupKey} className="bg-dark-700/20 rounded-xl p-3">
                <p className={`text-[10px] uppercase tracking-wider font-bold mb-2 ${meta?.color?.split(' ')[0] || 'text-gray-400'}`}>
                  {meta?.label || groupKey}
                </p>
                <div className="space-y-1">
                  {types.map((t) => (
                    <p key={t.id} className="text-[11px] font-mono text-gray-300">{t.id}</p>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const FORMAT_OPTIONS = [
  {
    id: 'raw',
    label: 'haythix raw JSON',
    description: 'Native payload {eventType, timestamp, data} — for your own server, webhook.site, n8n, Zapier, Make.com',
  },
  {
    id: 'discord',
    label: 'Discord',
    description: 'Wraps into {content: "..."} so Discord renders bot events as chat messages with emojis',
  },
  {
    id: 'slack',
    label: 'Slack',
    description: 'Wraps into {text: "..."} so Slack renders bot events as channel messages',
  },
  {
    id: 'telegram',
    label: 'Telegram',
    description: 'For Telegram bot sendMessage. URL: api.telegram.org/bot{TOKEN}/sendMessage?chat_id={ID}',
  },
  {
    id: 'custom',
    label: 'Custom template',
    description: 'Write your own message shape with {{eventType}}, {{data.symbol}}, etc. — works with ANY receiver',
  },
];

const CUSTOM_TEMPLATE_EXAMPLE = `{
  "title": "{{eventType}}",
  "symbol": "{{data.symbol}}",
  "side": "{{data.side}}",
  "price": "{{data.entryPrice}}",
  "exchange": "{{data.exchangeId}}"
}`;

function CreateForm({ eventTypes, onCancel, onCreated }) {
  const [name, setName] = useState('My Webhook');
  const [url, setUrl] = useState('');
  const [events, setEvents] = useState(['*']);
  const [generateSecret, setGenerateSecret] = useState(true);
  const [format, setFormat] = useState('raw');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [createdSecret, setCreatedSecret] = useState(null);

  const groups = groupEventTypes(eventTypes);

  const [template, setTemplate] = useState('');
  // Telegram helper inputs: when format=telegram, the user fills these and we
  // construct the URL automatically — much harder to get wrong than the raw URL.
  const [tgToken, setTgToken] = useState('');
  const [tgChatId, setTgChatId] = useState('');

  // Auto-detect format from URL pattern as a hint to the user
  useEffect(() => {
    if (!url) return;
    if (url.includes('discord.com/api/webhooks')) setFormat('discord');
    else if (url.includes('hooks.slack.com')) setFormat('slack');
    else if (url.includes('api.telegram.org/bot')) setFormat('telegram');
    // Don't auto-flip to raw — let user keep manual selection
  }, [url]);

  // When telegram fields change, auto-construct the URL
  useEffect(() => {
    if (format !== 'telegram') return;
    const t = tgToken.trim();
    const c = tgChatId.trim();
    if (t && c) {
      setUrl(`https://api.telegram.org/bot${t}/sendMessage?chat_id=${c}`);
    }
  }, [format, tgToken, tgChatId]);

  function toggleEvent(eventId) {
    if (eventId === '*') { setEvents(['*']); return; }
    setEvents((prev) => {
      const filtered = prev.filter((e) => e !== '*');
      return filtered.includes(eventId) ? filtered.filter((e) => e !== eventId) : [...filtered, eventId];
    });
  }
  function toggleAllInGroup(groupTypes) {
    const groupIds = groupTypes.map((t) => t.id);
    const allSelected = groupIds.every((id) => events.includes(id));
    setEvents((prev) => {
      const filtered = prev.filter((e) => e !== '*');
      if (allSelected) return filtered.filter((e) => !groupIds.includes(e));
      const set = new Set(filtered);
      groupIds.forEach((id) => set.add(id));
      return [...set];
    });
  }

  async function handleCreate() {
    if (format === 'custom' && !template.trim()) {
      setError('Custom format requires a template. Use {{variable}} placeholders — see the example below.');
      return;
    }
    setSaving(true); setError(null);
    try {
      const payload = { name, url, events, generateSecret, format };
      if (format === 'custom') payload.template = template;
      const res = await createOutboundWebhook(payload);
      const data = res?.data || res;
      if (data.secret && data.secretShownOnce) {
        setCreatedSecret(data.secret);
      } else {
        onCreated();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  if (createdSecret) {
    return (
      <div className="bg-gradient-to-br from-emerald-500/10 to-dark-800/60 border border-emerald-500/30 rounded-2xl p-5 space-y-4 shadow-xl shadow-emerald-500/10">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
            <KeyRound className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h3 className="text-base font-bold text-emerald-400">Webhook created — save your secret now</h3>
            <p className="text-xs text-gray-400 mt-1">
              This secret is shown <strong className="text-emerald-300">once</strong>. Use it to verify HMAC signatures (header <span className="text-cyan-300 font-mono">X-haythix-Signature: sha256=…</span>) on incoming requests. We will never show it again.
            </p>
          </div>
        </div>
        <div className="bg-dark-900 border border-emerald-500/20 rounded-xl p-3 font-mono text-xs break-all text-emerald-300 select-all">
          {createdSecret}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => navigator.clipboard.writeText(createdSecret)}
            className="flex-1 py-2.5 rounded-xl border border-dark-600 text-gray-300 hover:bg-dark-700 text-sm flex items-center justify-center gap-1.5"
          >
            <Copy className="w-3.5 h-3.5" /> Copy secret
          </button>
          <button
            onClick={onCreated}
            className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-400 hover:from-emerald-400 hover:to-teal-300 text-dark-900 font-semibold text-sm shadow-lg shadow-emerald-500/20"
          >
            I've saved it — done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-cyan-500/5 to-dark-800/60 border border-cyan-500/30 rounded-2xl p-5 space-y-4 shadow-xl shadow-cyan-500/5">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-bold text-white flex items-center gap-2">
          <Plus className="w-4 h-4 text-cyan-400" /> New webhook
        </h3>
        <button onClick={onCancel} className="text-gray-500 hover:text-white">
          <span className="text-xl leading-none">×</span>
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-gray-400 mb-1 block">Name</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={64}
            placeholder="My Slack alerts"
            className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2 text-white text-sm focus:border-cyan-500/40 focus:outline-none transition-colors"
          />
        </div>
        <div>
          <label className="text-xs text-gray-400 mb-1 flex items-center gap-1">
            URL (https://…)
            <InfoTip side="top">Paste Slack incoming webhook URL, Discord webhook URL, Zapier catch-hook, n8n webhook node, or your own endpoint. Discord/Slack URLs auto-set the format below.</InfoTip>
          </label>
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://discord.com/api/webhooks/…"
            className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2 text-white text-sm font-mono focus:border-cyan-500/40 focus:outline-none transition-colors"
          />
        </div>
      </div>

      {/* Payload format selector */}
      <div>
        <label className="text-xs text-gray-400 mb-1.5 flex items-center gap-1">
          Payload format
          <InfoTip side="right">raw = native JSON for your own server / Zapier / n8n. Discord/Slack/Telegram = pre-wrapped. Custom template = write your own shape for PagerDuty, Notion, Airtable, etc.</InfoTip>
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {FORMAT_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => setFormat(opt.id)}
              className={`text-left p-3 rounded-lg border transition-all ${
                format === opt.id
                  ? 'bg-cyan-500/10 border-cyan-400/40 ring-1 ring-cyan-400/30'
                  : 'bg-dark-700 border-dark-600 hover:border-dark-500'
              }`}
            >
              <p className={`text-xs font-bold mb-0.5 ${format === opt.id ? 'text-cyan-300' : 'text-white'}`}>{opt.label}</p>
              <p className="text-[10px] text-gray-500 leading-relaxed">{opt.description}</p>
            </button>
          ))}
        </div>

        {/* Format-specific guidance */}
        {format === 'telegram' && (
          <div className="mt-2 bg-cyan-500/5 border border-cyan-500/20 rounded-lg p-3 space-y-3">
            <div className="text-[11px] text-cyan-200/90 space-y-1">
              <p className="font-bold text-cyan-300">Telegram setup — fill the two fields below, the URL builds itself</p>
              <ol className="list-decimal list-inside space-y-0.5 text-gray-300 ml-1">
                <li>Message <span className="font-mono text-cyan-300">@BotFather</span> on Telegram → <span className="font-mono">/newbot</span> → copy your bot token (looks like <span className="font-mono text-cyan-300">123456789:ABC-DEF…</span>)</li>
                <li>Open Telegram → search your new bot → send it any message (e.g. "hi")</li>
                <li>Open <span className="font-mono text-cyan-300 break-all">https://api.telegram.org/bot{`<TOKEN>`}/getUpdates</span> in browser → find your numeric <span className="font-mono">chat.id</span></li>
              </ol>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] text-gray-400 mb-1 block">Bot token</label>
                <input
                  value={tgToken}
                  onChange={(e) => setTgToken(e.target.value.trim())}
                  placeholder="123456789:ABC-DEF…"
                  className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2 text-white text-xs font-mono focus:border-cyan-500/40 focus:outline-none"
                />
              </div>
              <div>
                <label className="text-[10px] text-gray-400 mb-1 block">Chat ID (numeric)</label>
                <input
                  value={tgChatId}
                  onChange={(e) => setTgChatId(e.target.value.trim())}
                  placeholder="123456789"
                  className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2 text-white text-xs font-mono focus:border-cyan-500/40 focus:outline-none"
                />
              </div>
            </div>
            {tgToken && tgChatId && (
              <div className="text-[10px] text-emerald-400/90 flex items-start gap-1.5 bg-emerald-500/5 border border-emerald-500/20 rounded p-2">
                <CheckCircle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                <span>URL constructed above — click <strong>Create webhook</strong> to register.</span>
              </div>
            )}
          </div>
        )}

        {format === 'custom' && (
          <div className="mt-2 space-y-2">
            <div className="bg-violet-500/5 border border-violet-500/20 rounded-lg p-3 text-[11px] text-violet-200/90 space-y-1.5">
              <p className="font-bold text-violet-300">Custom template</p>
              <p className="text-gray-300">Use <span className="font-mono text-violet-300">{`{{variable}}`}</span> placeholders. Available: <span className="font-mono text-violet-300">eventType, timestamp, userId, data.symbol, data.side, data.entryPrice, data.amount, data.exchangeId, data.filledPrice, data.tradeId, data.botId</span>.</p>
              <p className="text-gray-400">The rendered output is sent as-is to your receiver. Must be valid JSON if your receiver expects JSON.</p>
            </div>
            <div>
              <label className="text-[10px] text-gray-400 mb-1 flex items-center justify-between">
                <span>Template (max 5000 chars)</span>
                <button
                  type="button"
                  onClick={() => setTemplate(CUSTOM_TEMPLATE_EXAMPLE)}
                  className="text-violet-400 hover:text-violet-300"
                >
                  Insert example
                </button>
              </label>
              <textarea
                value={template}
                onChange={(e) => setTemplate(e.target.value.slice(0, 5000))}
                rows={6}
                placeholder='{"message": "{{eventType}} on {{data.symbol}}"}'
                className="w-full bg-dark-900 border border-dark-600 rounded-lg p-2 text-violet-200 text-xs font-mono resize-none focus:border-violet-500/40 focus:outline-none transition-colors"
              />
              <p className="text-[10px] text-gray-500 mt-1 text-right">{template.length}/5000</p>
            </div>
          </div>
        )}

        {(format === 'discord' || format === 'slack' || format === 'telegram') && (
          <p className="text-[10px] text-amber-300/80 mt-2 flex items-start gap-1.5">
            <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" />
            Note: {format === 'discord' ? 'Discord' : format === 'slack' ? 'Slack' : 'Telegram'} doesn't verify HMAC signatures, so the secret is purely defensive (still useful if you ever switch to a raw receiver).
          </p>
        )}
      </div>

      {/* Event subscriptions grouped by domain */}
      <div>
        <label className="text-xs text-gray-400 mb-1.5 block">Subscribe to events</label>
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => toggleEvent('*')}
            className={`w-full text-left p-2.5 rounded-lg border transition-all flex items-center gap-2 ${
              events.includes('*')
                ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-300'
                : 'bg-dark-700 border-dark-600 text-gray-400 hover:border-dark-500'
            }`}
          >
            <span className={`w-4 h-4 rounded border flex items-center justify-center ${events.includes('*') ? 'bg-emerald-500 border-emerald-500' : 'border-dark-500'}`}>
              {events.includes('*') && <CheckCircle className="w-3 h-3 text-dark-900" />}
            </span>
            <span className="font-bold text-sm">All events (recommended)</span>
            <span className="text-[10px] text-gray-500 ml-auto">subscribe to every event we fire</span>
          </button>

          {!events.includes('*') && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {Object.entries(groups).map(([groupKey, types]) => {
                if (types.length === 0) return null;
                const meta = GROUP_META[groupKey];
                const groupIds = types.map((t) => t.id);
                const allSelected = groupIds.every((id) => events.includes(id));
                return (
                  <div key={groupKey} className="bg-dark-700/30 border border-dark-700/50 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className={`text-[10px] uppercase tracking-wider font-bold ${meta?.color?.split(' ')[0] || 'text-gray-400'}`}>
                        {meta?.label || groupKey}
                      </p>
                      <button
                        type="button"
                        onClick={() => toggleAllInGroup(types)}
                        className="text-[10px] text-gray-500 hover:text-white"
                      >
                        {allSelected ? 'Unselect all' : 'Select all'}
                      </button>
                    </div>
                    <div className="space-y-1">
                      {types.map((t) => (
                        <label key={t.id} className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer hover:text-white">
                          <input
                            type="checkbox"
                            checked={events.includes(t.id)}
                            onChange={() => toggleEvent(t.id)}
                            className="rounded"
                          />
                          <span className="font-mono text-[11px]">{t.id}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <label className="flex items-start gap-2 text-xs text-gray-300 cursor-pointer bg-dark-700/30 rounded-lg p-3 border border-dark-700/50">
        <input
          type="checkbox"
          checked={generateSecret}
          onChange={(e) => setGenerateSecret(e.target.checked)}
          className="mt-0.5 rounded"
        />
        <span>
          <span className="font-medium text-white">Generate HMAC-SHA256 secret</span>
          <span className="block text-[10px] text-gray-500 mt-0.5">
            Receiver can verify every webhook came from us by checking the <span className="font-mono text-cyan-300">X-haythix-Signature</span> header. Strongly recommended.
          </span>
        </span>
      </label>

      {error && (
        <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-3 text-red-400 text-xs flex items-start gap-2">
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex gap-2 pt-1">
        <button
          onClick={onCancel}
          className="flex-1 py-2.5 rounded-xl border border-dark-600 text-gray-300 hover:bg-dark-700 text-sm"
        >
          Cancel
        </button>
        <button
          onClick={handleCreate}
          disabled={saving || !url || events.length === 0}
          className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-400 hover:from-cyan-400 hover:to-blue-300 disabled:from-dark-600 disabled:to-dark-600 disabled:text-gray-500 text-dark-900 font-semibold text-sm flex items-center justify-center gap-2 shadow-lg shadow-cyan-500/20"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          {saving ? 'Creating…' : 'Create webhook'}
        </button>
      </div>
    </div>
  );
}
